import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Headset, Inbox, PenLine, Ticket } from "lucide-react";

import { cn } from "@/lib/utils";
import useAuthStore from "@/store/use-auth-store";

import { isAdminUser } from "../utils/admin";

const TabLink: React.FC<{
  to: string;
  isActive: boolean;
  icon: React.ReactNode;
  label: string;
}> = ({ to, isActive, icon, label }) => {
  return (
    <Link
      to={to}
      className={cn(
        "flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors sm:gap-2 sm:px-4 sm:py-2 sm:text-sm",
        isActive
          ? "bg-background text-foreground shadow-sm dark:bg-gray-800"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      {icon}
      {label}
    </Link>
  );
};

export const SupportHeaderSection: React.FC = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const isLoggedIn = !!user?._id;
  const isAdmin = isAdminUser(user);

  const isTicketsRoute = pathname.startsWith("/support/tickets");
  const isAllScope = isTicketsRoute && searchParams.get("scope") === "all";

  return (
    <div className="flex flex-col gap-3 sm:gap-6">
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-indigo-500/10 dark:bg-indigo-500/20 sm:flex">
          <Headset className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div className="flex flex-col gap-0.5 sm:gap-1">
          <h1 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl lg:text-3xl">
            {t("support.hero.title")}
          </h1>
          <p className="max-w-2xl text-xs text-muted-foreground sm:text-sm lg:text-base">
            {t("support.hero.subtitle")}
          </p>
        </div>
      </div>

      {/* Sekmeler yalnızca giriş yapmış kullanıcılara gösterilir */}
      {isLoggedIn && (
        <div className="flex w-fit items-center gap-1 overflow-x-auto rounded-xl bg-muted p-1">
          <TabLink
            to="/support"
            isActive={!isTicketsRoute}
            icon={<PenLine className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
            label={t("support.tabs.newRequest")}
          />
          <TabLink
            to="/support/tickets"
            isActive={isTicketsRoute && !isAllScope}
            icon={<Ticket className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
            label={t("support.tabs.myTickets")}
          />
          {/* Admin: paneldeki tüm destek talepleri */}
          {isAdmin && (
            <TabLink
              to="/support/tickets?scope=all"
              isActive={isAllScope}
              icon={<Inbox className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
              label={t("support.tabs.allTickets")}
            />
          )}
        </div>
      )}
    </div>
  );
};
